import { DesignerSettings } from '@decisions/server/DecisionsFramework/Design';
import {
  DecisionsSettingInfo,
  FeatureFlags,
  PortalOptions,
  PortalSideBarDefaultPanel,
  UserPortalSettingInfo,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal';
import {
  getDesignerSettings as fetchDesignerSettings,
  getDecisionsSettingInfo as fetchDecisionsSettingInfo,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal/PortalSettingsService';
import { GetState, SetState } from 'types/zustand';
import create from 'zustand';
import { registerForEventAndSubscribe } from 'utils/ClientEventUtils';
import { getChromeSettingFromString, getTopBarState as getTopBarStateUtil } from 'utils/ChromeParameterUtils';
import { InitialUrlParams } from 'components/Router/RouterUtils';
import produce from 'immer';
import { ChromeSetting } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/ChromeSetting';

export const DESIGNER_SETTINGS_SAVED_KEY = 'DesignerSettings.Saved';

export function createSettingsState() {
  return {
    isStudio: false,
    // these are possibly undefined as they are lazy-loaded.
    designerSettings: undefined as DesignerSettings | undefined,
    decisionsSettingInfo: undefined as DecisionsSettingInfo | undefined,
    portalSettings: getDefaultPortalSettings(),
  };
}

export function createSettingsActions(get: GetState<SettingsStore>, set: SetState<SettingsStore>) {
  if (!DEV_SERVER && !IS_TEST) {
    registerForEventAndSubscribe(DESIGNER_SETTINGS_SAVED_KEY, 'settings-store', e => {
      set({ designerSettings: (e as any).detail as DesignerSettings });
    });
  }

  return {
    async getDecisionsSettings() {
      const { decisionsSettingInfo } = get();
      if (decisionsSettingInfo) {
        return decisionsSettingInfo;
      }

      try {
        const decisionsSettingInfo = await fetchDecisionsSettingInfo();
        set({ decisionsSettingInfo });
        return decisionsSettingInfo;
      } catch (error) {
        console.error('Unable to load Decisions Setting Info', error);
      }
    },
    async getDesignerSettings() {
      const { designerSettings } = get();
      if (designerSettings) {
        return designerSettings;
      }

      try {
        const designerSettings = await fetchDesignerSettings();
        set({ designerSettings });
        return designerSettings;
      } catch (error) {
        console.error('Unable to load Designer Settings', error);
      }
    },
    async initializeSettings({ isStudio }: PortalOptions) {
      if (isStudio) {
        get().actions.getDesignerSettings();
      }
      get().actions.getDecisionsSettings();
    },

    /**
     * Set portal settings.
     * @param portalSettings to assign
     * @param chromeSetting optional, to override what was there, e.g. make URL source of truth on portal load.
     */
    setPortalSettingInfo: (portalSettings: UserPortalSettingInfo, chrome?: ChromeSetting, isStudio = false) =>
      set(
        produce<SettingsStore>(state => {
          state.isStudio = isStudio;
          state.portalSettings = portalSettings;
          if (typeof chrome !== 'undefined') {
            state.portalSettings.Chrome = chrome;
          }
        })
      ),

    setImpersonatedUser: (impersonatedUser: string) =>
      set(
        produce<SettingsStore>(state => {
          state.portalSettings.ImpersonatedUser = impersonatedUser;
        })
      ),

    updateHomeFolderId: (newHomeFolderId: string) =>
      set(
        produce<SettingsStore>(state => {
          state.portalSettings.HomeFolderId = newHomeFolderId;
        })
      ),
  };
}

export type SettingsStore = ReturnType<typeof createSettingsState> & {
  actions: ReturnType<typeof createSettingsActions>;
};

export function getPortalSettings() {
  return useSettingsStore.getState().portalSettings;
}

export const useSettingsStore = create<SettingsStore>((set, get) => {
  return {
    ...createSettingsState(),
    actions: createSettingsActions(get, set),
  };
});

// #region selector functions
export function getDesignerSettings(state: SettingsStore) {
  return state.designerSettings;
}

export function getDecisionsSettings(state: SettingsStore) {
  return state.decisionsSettingInfo;
}

export function getPortalSlogan(state: SettingsStore) {
  return state.portalSettings.Settings.SloganText;
}

export function getUserDisplayName(state: SettingsStore) {
  return state.portalSettings.UserDisplayName;
}

export function getShowCurrentFolderInHeader(state: SettingsStore) {
  return state.portalSettings.Settings.ShowCurrentFolderNameInHeader;
}

export function getShowCreateRootFolder(state: SettingsStore) {
  return state.portalSettings.Settings.ShowCreateRootFolder;
}

export function getCanAddRootFolder(state: SettingsStore) {
  return state.portalSettings.CanAddRootFolder;
}

export function getShowSidebar(state: SettingsStore) {
  const frameStyle = getFrameStyle(state);
  switch (frameStyle) {
    case ChromeSetting.Off:
    case ChromeSetting.Small:
    case ChromeSetting.TopBarOnly:
      return false;
    default:
      return true;
  }
}

export function getTopBarVisible(state: SettingsStore) {
  const frameStyle = getFrameStyle(state);
  switch (frameStyle) {
    case ChromeSetting.Off:
    case ChromeSetting.NoTopBarHasFolderTree:
    case ChromeSetting.NoTopBarFolderTreeCollapsed:
      return false;
    default:
      return true;
  }
}

export function getTopBarState(state: SettingsStore) {
  return getTopBarStateUtil(getFrameStyle(state));
}

export function getFrameStyleIsOff(state: SettingsStore) {
  return getFrameStyle(state) === ChromeSetting.Off;
}

export function getUseSmallHeader(state: SettingsStore) {
  return getFrameStyle(state) === ChromeSetting.Small;
}

export function getImpersonatedUser(state: SettingsStore) {
  return state.portalSettings.ImpersonatedUser;
}

export function getHideWorkflowCatalog(state: SettingsStore) {
  return state.portalSettings.Settings.HideWorkflowCatalog;
}

export function getShowRecentTab(state: SettingsStore) {
  return state.portalSettings.Settings.ShowRecentFolder;
}

export function getShowFavorites(state: SettingsStore) {
  return state.portalSettings.Settings.ShowFavorites;
}

export function getShowFolders(state: SettingsStore) {
  return state.portalSettings.Settings.ShowFoldersCatalog;
}

export function getFrameStyle(state: SettingsStore) {
  return state.portalSettings.Chrome;
}

export function getCollapsedTreeOnStartStudio(state: SettingsStore) {
  return state.portalSettings.Settings.CollapsedTreeOnStartStudio;
}

export function getCollapsedTreeOnStartUserPortal(state: SettingsStore) {
  return state.portalSettings.Settings.CollapsedTreeOnStartUserPortal;
}

export function getHomeFolderId(state: SettingsStore) {
  return state.portalSettings.HomeFolderId;
}

export function getDefaultFolderId(state: SettingsStore) {
  return state.portalSettings.DefaultFolderId || state.portalSettings.HomeFolderId;
}

export function getHideMobileWorkflows(state: SettingsStore) {
  return state.portalSettings.Settings.HideMobilePortalWorkflows;
}

export function getHideMobileInbox(state: SettingsStore) {
  return state.portalSettings.Settings.HideMobilePortalInbox;
}

export function getHideMobileFolders(state: SettingsStore) {
  return state.portalSettings.Settings.HideMobilePortalFolders;
}

export function getShowMobileFooter(state: SettingsStore) {
  return (
    [getHideMobileWorkflows(state), getHideMobileInbox(state), getHideMobileFolders(state)].filter(hide => !hide)
      .length > 1
  );
}

export function allMobileTabsHidden(state: SettingsStore) {
  return getHideMobileWorkflows(state) && getHideMobileInbox(state) && getHideMobileFolders(state);
}

export function getDefaultMobileActiveTab(state: SettingsStore) {
  return getHideMobileInbox(state)
    ? getHideMobileWorkflows(state)
      ? getHideMobileFolders(state)
        ? undefined // error
        : 'folders' // third
      : 'workflows' // second
    : 'inbox'; // first
}

export function getLogoUrl(state: SettingsStore) {
  // this is a byte[] in the server, wat?
  return state.portalSettings.Settings.ShowLogoInHeader
    ? state.portalSettings.Settings.Logo
      ? `data:image/png;base64,${state.portalSettings.Settings.Logo}`
      : `${virtualPath}/images/logo.png` // CSS takes care of this if not otherwise defined?
    : '';
}

export function getSearchPageSize(state: SettingsStore) {
  return state.portalSettings.Settings.InlineSearchResultLimit;
}

export function getRootRootWorkflowCatalogItems(state: SettingsStore) {
  return state.portalSettings.Settings.RootWorkflowCatalogItems;
}

export function getShowLockPortalAction(state: SettingsStore) {
  return state.portalSettings.Settings.ShowLockPortalAction;
}

export function getShowSettingsInHeader(state: SettingsStore) {
  return state.portalSettings.ShowSettingsInHeader;
}

export function getShowAccountInHeader(state: SettingsStore) {
  return state.portalSettings.ShowAccountInHeader;
}

export function getShowAboutInHeader(state: SettingsStore) {
  return state.portalSettings.ShowAboutInHeader;
}

export function getShowLogoutInHeader(state: SettingsStore) {
  return state.portalSettings.ShowLogoutInHeader;
}

export function getTimeZoneInformation(state: SettingsStore) {
  return state.portalSettings.TimeZoneInformation;
}

export function getCurrentUserEmail(state: SettingsStore) {
  return state.portalSettings.CurrentUserEmail;
}

export function getNotificationTimeout(state: SettingsStore) {
  return state.portalSettings.Settings.NotificationItemsTimeout;
}

export function getDisableSupportChat(state: SettingsStore) {
  return state.portalSettings.Settings.DisableSupportChat;
}

export function getDefaultActiveTab(state: SettingsStore) {
  return state.portalSettings.Settings.PortalSideBarDefaultPanel;
}

export function getFeatureFlags(state: SettingsStore): FeatureFlags {
  return state.portalSettings.FeatureFlags;
}

export function hasHelpCenterTools(state: SettingsStore): boolean {
  return state.portalSettings.HasHelpCenterTools;
}

//#endregion selectors

//#region helpers

function getDefaultPortalSettings() {
  return ({
    UserDisplayName: 'User',
    StartupPopUpPage: null,
    Chrome: getChromeSettingFromString(InitialUrlParams.Chrome || InitialUrlParams.chrome),
    Settings: {
      ShowLogoInHeader: false,
      Logo: null,
      SloganText: 'Decisions Studio',
      ActionLocation: 0,
      BrowsePageName: 'Decisions Studio',
      LockPortalShortcut: { Control: false, Alt: false, Shift: false, KeyName: '' },
      AutoLockReloginAppearance: 0,
      InlineSearchResultLimit: 15,
      MinPortalHeight: 600.0,
      MinPortalWidth: 1024.0,
      IsShowDevelopmentAction: false,
      NotificationTimeout: 5000.0,
      NotificationItemsTimeout: 120000.0,
      ShowLockPortalAction: true,
      ShowDetailLinkOnErrorDialog: true,
      ExtensionsNotAllowed: ['bat', 'exe', 'ps1', 'com'],
      ShowCurrentFolderNameInHeader: true,
      ShowFavorites: true,
      HideWorkflowCatalog: false,
      HideMobilePortalInbox: false,
      HideMobilePortalWorkflows: false,
      HideMobilePortalFolders: false,
      ShowFoldersCatalog: true,
      ShowRecentFolder: true,
      HideTopBarSearch: true, // default to true so we don't get a flash of it when its hidden...
      PortalSideBarDefaultPanel: PortalSideBarDefaultPanel.Folders,
      CollapsedTreeOnStartStudio: false,
      CollapsedTreeOnStartUserPortal: false,
      ShowCreateRootFolder: true,
      UserBackAccountKey: null,
      RootWorkflowCatalogItems: 'SERVICE CATALOG DEFAULT FOLDER',
      DoNotAutoGenerateThemeCss: false,
      TimeFormat: 0,
      AutoSubmitInterval: 500,
      ChatServiceURL: 'https://product-help.decisions.com',
      DisableSupportChat: false,
      SupportEmailAddress: 'support@decisions.com',
      AllowMultipleFileExtensions: true,
      ShowUserHomeFolder: true,
    },
    HomeFolderId: '',
    DefaultFolderId: '',
    TimeZoneInformation: 'Eastern Standard Time',
    ShowSettingsInHeader: true,
    ShowAccountInHeader: true,
    ShowAboutInHeader: true,
    ShowLockInHeader: true,
    ShowLogoutInHeader: true,
    CanAddRootFolder: true,
    CanSetFavoriteForAllUsers: true,
    CurrentUserEmail: '',
    ImpersonatedUser: null,
    CurrentInstanceName: null,
    InitialFolderId: null,
    UseTimeZoneTranslation: false,
    UseDefaultIconsInFolderTree: true,
    LicenseType: 'StandardProduction',
    HomeFolderName: '',
    PinnedFolderId: '',
    FeatureFlags: {} as any,
  } as unknown) as UserPortalSettingInfo;
}

//#endregion helpers
